import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FactionEnum } from "@/domains/faction/constants/factionEnum";
import { ParticipantRow } from "@/domains/faction/constants/participantRow";
import { useAddInBlackList } from "@/domains/faction/hooks/useAddInBlackList";
import { useChangeDepartment } from "@/domains/faction/hooks/useChangeDepartment";
import { useChangePosition } from "@/domains/faction/hooks/useChangePosition";
import { useDismissMember } from "@/domains/faction/hooks/useDismissMember";
import useGetInfo from "@/domains/faction/hooks/useGetInfo";
import { useGiveAward } from "@/domains/faction/hooks/useGiveAward";
import { useRemoveFromBlackList } from "@/domains/faction/hooks/useRemoveFromBlackList";
import { useUpDownRank } from "@/domains/faction/hooks/useUpDownRank";
import FactionConfirmModal from "@/domains/faction/components/FactionConfirmModal";
import FactionInputModal from "@/domains/faction/components/FactionInputModal";
import FactionSelectModal from "@/domains/faction/components/FactionSelectModal";
import FactionSliderModal from "@/domains/faction/components/FactionSliderModal";

type Props = {
  selectedRowInfo: ParticipantRow;
  faction: FactionEnum;
  isUpModal: boolean;
  setIsUpModal: (flag: boolean) => void;
  isDownModal: boolean;
  setIsDownModal: (flag: boolean) => void;
  isBonusesModal: boolean;
  setIsBonusesModal: (flag: boolean) => void;
  isPostModal: boolean;
  setIsPostModal: (flag: boolean) => void;
  isDepartmentModal: boolean;
  setIsDepartmentModal: (flag: boolean) => void;
  isBlackListModal: boolean;
  setIsBlackListModal: (flag: boolean) => void;
  isRemoveFromBlackListModal: boolean;
  setIsRemoveFromBlackListModal: (flag: boolean) => void;
  isDismissModal: boolean;
  setIsDismissModal: (flag: boolean) => void;
  onBack: () => void;
};

export default function FactionColeaguesModalsContainer({
  selectedRowInfo,
  faction,
  isUpModal,
  setIsUpModal,
  isDownModal,
  setIsDownModal,
  isBonusesModal,
  setIsBonusesModal,
  isPostModal,
  setIsPostModal,
  isDepartmentModal,
  setIsDepartmentModal,
  isBlackListModal,
  setIsBlackListModal,
  isRemoveFromBlackListModal,
  setIsRemoveFromBlackListModal,
  isDismissModal,
  setIsDismissModal,
  onBack,
}: Props) {
  const { t } = useTranslation("faction");
  const { data: factionInfo } = useGetInfo();

  const [activePositionId, setActivePositionId] = useState(selectedRowInfo?.rankPositionId);
  const [activeDepartmentId, setActiveDepartmentId] = useState(selectedRowInfo?.departmentId);

  const positionsList = useMemo(() => {
    if (!factionInfo?.rankPositions) return [];
    return factionInfo.rankPositions.map((item) => ({
      id: item.id,
      name: item.name,
      isActive: item.id === activePositionId,
    }));
  }, [factionInfo?.rankPositions, activePositionId]);

  const departmentsList = useMemo(() => {
    if (!factionInfo?.departments) return [];
    return factionInfo.departments.map((item) => ({
      id: item.id,
      name: item.name,
      isActive: item.id === activeDepartmentId,
    }));
  }, [factionInfo?.departments, activeDepartmentId]);

  const onChangePositions = useCallback((id: number) => {
    setActivePositionId(id);
  }, []);

  const onChangeDepartments = useCallback((id: number) => {
    setActiveDepartmentId(id);
  }, []);

  const onClosePositionsModal = useCallback(() => {
    setActivePositionId(selectedRowInfo?.rankPositionId);
    setIsPostModal(false);
  }, [selectedRowInfo?.rankPositionId, setIsPostModal]);

  const onCloseDepartmentsModal = useCallback(() => {
    setActiveDepartmentId(selectedRowInfo?.departmentId);
    setIsDepartmentModal(false);
  }, [selectedRowInfo?.departmentId, setIsDepartmentModal]);

  const { mutate: giveAward, isPending: isGiveAwardPending } = useGiveAward();
  const { mutate: addInBlackList, isPending: isAddInBlackListPending } = useAddInBlackList();
  const { mutate: removeFromBlackList, isPending: isRemoveFromBlackListPending } =
    useRemoveFromBlackList();
  const { mutate: changePosition, isPending: isChangePositionPending } = useChangePosition();
  const { mutate: changeDepartment, isPending: isChangeDepartmentPending } = useChangeDepartment();
  const { mutate: dismissMember, isPending: isDismissMemberPending } = useDismissMember();
  const { mutate: upDownRank, isPending: isUpDownRankPending } = useUpDownRank();

  const onUpRank = useCallback(() => {
    if (selectedRowInfo?.id) {
      upDownRank({
        memberId: selectedRowInfo.id,
        direction: 1,
        cb: () => setIsUpModal(false),
      });
    }
  }, [selectedRowInfo?.id, setIsUpModal, upDownRank]);

  const onDownRank = useCallback(() => {
    if (selectedRowInfo?.id) {
      upDownRank({
        memberId: selectedRowInfo.id,
        direction: -1,
        cb: () => setIsDownModal(false),
      });
    }
  }, [selectedRowInfo?.id, setIsDownModal, upDownRank]);

  const onGiveAward = useCallback(
    (amount: number) => {
      if (selectedRowInfo?.id && amount) {
        giveAward({
          memberId: selectedRowInfo.id,
          amount: amount * 1000,
          cb: () => setIsBonusesModal(false),
        });
      }
    },
    [giveAward, selectedRowInfo?.id, setIsBonusesModal],
  );

  const onAddInBlackList = useCallback(
    (reason: string) => {
      if (selectedRowInfo?.characterId && reason) {
        addInBlackList({
          characterId: selectedRowInfo.characterId,
          reason,
          cb: () => setIsBlackListModal(false),
        });
      }
    },
    [addInBlackList, selectedRowInfo?.id, setIsBlackListModal],
  );

  const onRemoveFromBlackList = useCallback(() => {
    if (selectedRowInfo?.characterId) {
      removeFromBlackList({
        characterId: selectedRowInfo.characterId,
        cb: () => setIsRemoveFromBlackListModal(false),
      });
    }
  }, [removeFromBlackList, selectedRowInfo?.characterId, setIsRemoveFromBlackListModal]);

  const onChangePosition = useCallback(
    (rankPositionId: number) => {
      if (selectedRowInfo?.id && rankPositionId) {
        changePosition({
          memberId: selectedRowInfo.id,
          rankPositionId,
          cb: () => setIsPostModal(false),
        });
      }
    },
    [changePosition, selectedRowInfo?.id, setIsPostModal],
  );

  const onChangeDepartment = useCallback(
    (departmentId: number) => {
      if (selectedRowInfo?.id && departmentId) {
        changeDepartment({
          memberId: selectedRowInfo.id,
          departmentId,
          cb: () => setIsDepartmentModal(false),
        });
      }
    },
    [changeDepartment, selectedRowInfo?.id, setIsDepartmentModal],
  );

  const onDismissMember = useCallback(() => {
    if (selectedRowInfo?.id) {
      dismissMember({
        memberId: selectedRowInfo.id,
        cb: () => {
          setIsDismissModal(false);
          onBack();
        },
      });
    }
  }, [dismissMember, onBack, selectedRowInfo?.id, setIsDismissModal]);

  useEffect(() => {
    setActivePositionId(selectedRowInfo?.rankPositionId);
    setActiveDepartmentId(selectedRowInfo?.departmentId);
  }, [selectedRowInfo?.rankPositionId, selectedRowInfo?.departmentId]);

  return (
    <>
      {isUpModal && (
        <FactionConfirmModal
          title={t("faction.modals.upRank.title")}
          description={t("faction.modals.upRank.description")}
          onClose={() => setIsUpModal(false)}
          onConfirm={onUpRank}
          isConfirming={isUpDownRankPending}
          faction={faction}
        />
      )}

      {isDownModal && (
        <FactionConfirmModal
          title={t("faction.modals.downRank.title")}
          description={t("faction.modals.downRank.description")}
          onClose={() => setIsDownModal(false)}
          onConfirm={onDownRank}
          isConfirming={isUpDownRankPending}
          faction={faction}
        />
      )}

      {isBonusesModal && (
        <FactionSliderModal
          title={t("faction.modals.giveBobuses.title")}
          text={t("faction.modals.giveBobuses.bonusesSum")}
          btnText={t("faction.modals.giveBobuses.giveBobuses")}
          onClose={() => setIsBonusesModal(false)}
          onConfirm={onGiveAward}
          isConfirming={isGiveAwardPending}
          faction={faction}
        />
      )}

      {isPostModal && (
        <FactionSelectModal
          title={t("faction.modals.changePost.title")}
          onClose={onClosePositionsModal}
          onConfirm={onChangePosition}
          onChangeSelect={onChangePositions}
          isConfirming={isChangePositionPending}
          faction={faction}
          selectsList={positionsList}
          currentId={selectedRowInfo?.rankPositionId}
        />
      )}

      {isDepartmentModal && (
        <FactionSelectModal
          title={t("faction.modals.changeDepartament.title")}
          onClose={onCloseDepartmentsModal}
          onConfirm={onChangeDepartment}
          onChangeSelect={onChangeDepartments}
          isConfirming={isChangeDepartmentPending}
          faction={faction}
          selectsList={departmentsList}
          currentId={selectedRowInfo?.departmentId}
        />
      )}

      {isBlackListModal && (
        <FactionInputModal
          title={t("faction.modals.blackList.title")}
          onClose={() => setIsBlackListModal(false)}
          onConfirm={onAddInBlackList}
          isConfirming={isAddInBlackListPending}
          faction={faction}
        />
      )}

      {isRemoveFromBlackListModal && (
        <FactionConfirmModal
          title={t("faction.modals.exitBlackList.title")}
          description={t("faction.modals.exitBlackList.description")}
          onClose={() => setIsRemoveFromBlackListModal(false)}
          onConfirm={onRemoveFromBlackList}
          isConfirming={isRemoveFromBlackListPending}
          faction={faction}
        />
      )}

      {isDismissModal && (
        <FactionConfirmModal
          title={t("faction.modals.dismissUser.title")}
          description={t("faction.modals.dismissUser.description")}
          onClose={() => setIsDismissModal(false)}
          onConfirm={onDismissMember}
          isConfirming={isDismissMemberPending}
          faction={faction}
        />
      )}
    </>
  );
}
